/**
 * Consent banner (consentmanager.net) branding for white-label pages.
 *
 * One CMP configuration serves the whole storefront — `pages/_app.tsx` loads
 * it with a single `cmp_cdid`, outside the branch that picks `HeadWL` over
 * `Head`. Its design is Remi's: lime `#DAFF3D` buttons, teal `#144F55` links,
 * warm-white `#F6F5EE` box. On a payer page the consent banner is the very
 * first thing a member sees, and it was showing Remi's colours over BIG /
 * SBK / mhplus / IKK branding.
 *
 * The CMP offers no design that can be driven from the client record (designs
 * are built by hand in its backend, so a new client would never be covered),
 * so the presentation is corrected here from the colours Operations already
 * stores on the BusinessClient:
 *
 *   - `buildCmpBrandingCss()` restates the design's colour declarations with
 *     the client's palette. The CMP injects its stylesheet LAST at runtime and
 *     marks nearly every declaration `!important`, so ours cannot win on
 *     ordering — every selector therefore carries an `html body` prefix, which
 *     raises specificity above the vendor's single-class rules while staying
 *     below an id. Their sheet holds no id-based colour rules (only
 *     `#cmpbox{padding}` and `div#cmpwrapper{display}`), so this is enough.
 *     Every selector and property below was read off the live injected
 *     stylesheet on www.myremi.com, not guessed.
 *   - `buildCmpClientConfig()` sets the CMP's own client-side variables for
 *     the legal links, so the banner's "Datenschutzerklärung" points where the
 *     page footer points instead of always at learn.myremi.com.
 *
 * Only what belongs to the client is tinted. Hairlines, secondary text and
 * the inactive toggle stay neutral, per the white-label styling rule — a
 * payer palette is two or three colours and does not survive being stretched
 * over a whole component.
 */

/** The BusinessClient fields this reads. Same shape as everywhere else. */
export interface CmpBrandingClient {
  privacyUrl?: string | null;
  imprintUrl?: string | null;
  termsOfServiceUrl?: string | null;
  bodyTextColor?: string | null;
  actionBackgroundColor?: string | null;
  actionTextColor?: string | null;
  /** Injected by `brandClientOverrides` only when the HPV layout is on. */
  brandPrimary?: string | null;
  brandPrimaryOn?: string | null;
  brandInk?: string | null;
  brandSurfaceWhite?: string | null;
}

export interface CmpPalette {
  /** Banner and settings-dialog background. */
  surface: string;
  /** Headline and body text. */
  ink: string;
  /** Accept button / active toggle. */
  button: string;
  /** Text on the accept button. */
  buttonOn: string;
  /** Hairlines and table borders — deliberately neutral. */
  line: string;
  /** Search / row highlight — deliberately neutral. */
  highlight: string;
  /** The off toggle track — deliberately neutral, so on and off stay
      distinguishable whatever the payer's palette is. */
  toggleOff: string;
}

const NEUTRAL = {
  surface: '#FFFFFF',
  ink: '#1C1C1C',
  buttonOn: '#FFFFFF',
  line: '#D9D9D9',
  highlight: '#F2F2F2',
  toggleOff: '#D9D9D9'
}

/**
 * Colours are interpolated into a `<style>` element, so anything that is not
 * literally a hex colour is dropped rather than escaped — a stray `</style>`
 * out of a CMS field would otherwise end the block.
 */
const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

function firstColor(...candidates: Array<string | null | undefined>): string | null {
  for (const candidate of candidates) {
    const value = typeof candidate === 'string' ? candidate.trim() : ''
    if (HEX_COLOR.test(value)) return value
  }
  return null
}

/**
 * Same precedence as `LayoutWLRedesign.buildBrandStyle`, so the banner is
 * tinted like the page under it: the HPV layout's `brand*` fields win where
 * they exist, everything else keeps mapping the long-standing
 * `actionBackgroundColor` / `bodyTextColor`. `brandPrimary` is only ever
 * injected when `useProjectContent` is on, so its presence IS that flag.
 */
export function resolveCmpPalette(
  client: CmpBrandingClient | null | undefined
): CmpPalette | null {
  if (client == null) return null

  const redesign = firstColor(client.brandPrimary) != null

  const button = firstColor(
    redesign ? client.brandPrimary : null,
    client.actionBackgroundColor
  )
  const ink = firstColor(
    redesign ? client.brandInk : null,
    client.bodyTextColor
  )

  // Nothing usable on the record: leave the CMP exactly as it is rather than
  // repaint it from defaults we made up.
  if (button == null && ink == null) return null

  return {
    surface: firstColor(redesign ? client.brandSurfaceWhite : null) ?? NEUTRAL.surface,
    ink: ink ?? NEUTRAL.ink,
    button: button ?? (ink as string),
    buttonOn:
      firstColor(
        redesign ? client.brandPrimaryOn : null,
        client.actionTextColor
      ) ?? NEUTRAL.buttonOn,
    line: NEUTRAL.line,
    highlight: NEUTRAL.highlight,
    toggleOff: NEUTRAL.toggleOff
  }
}

/**
 * The override sheet. Mirrors the vendor design's own colour declarations
 * one-for-one — nothing here changes layout, spacing or behaviour.
 */
export function buildCmpBrandingCss(
  client: CmpBrandingClient | null | undefined
): string {
  const p = resolveCmpPalette(client)
  if (p == null) return ''

  // Toggle SVGs. The state lives in the path's colour class, NOT in an aria
  // attribute — `cmpsvgredfill` is the off track, and green/black/gray/silver
  // are the on and locked-on tracks, which the design paints in one single
  // teal. (The CMP backend's own custom CSS still carries an
  // `a[aria-checked="true"]` rule for the on state; this version of the CMP
  // renders no `aria-checked` at all, so that rule matches nothing and is not
  // reproduced here.) Same split as the design: one client colour for every
  // on state, a neutral for off — verified against a mandatory-on and an
  // opt-in-off toggle in the live consent dialog.
  const toggle = (states: string[], color: string) =>
    states
      .map(
        (state) =>
          `html body .cmptogglespan svg .cmpsvg${state}fill{fill:${color} !important}` +
          `html body .cmptogglespan svg .cmpsvg${state}stroke{stroke:${color} !important}`
      )
      .join('')
  const toggleTracks =
    toggle(['green', 'black', 'gray', 'gray2', 'silver', 'silver2'], p.button) +
    toggle(['red'], p.toggleOff)

  return (
    // Base theme. Everything the design drives through var() follows from here.
    `html body .cmpstyleroot,html body .cmpstyleroot *{` +
      `--clrBackground:${p.surface};` +
      `--clrHeadline:${p.ink};` +
      `--clrText:${p.ink};` +
      `--clrTextSecondary:${p.line};` +
      `--clrButton:${p.button};` +
      `--clrButtonText:${p.buttonOn};` +
      `--clrLink:${p.ink};` +
      `--clrHighlight:${p.highlight}}` +

    // Banner box: warm white + lime/teal borders.
    `html body .cmpbox{background-color:${p.surface} !important;` +
      `border-top-color:${p.line} !important;border-right-color:${p.line} !important;` +
      `border-bottom-color:${p.line} !important;border-left-color:${p.line} !important}` +

    // Headline, body copy, links, toggle labels, recall label.
    `html body .cmpboxhl,html body .cmpboxtxt,html body .cmpmore,` +
      `html body .cmpmorelink,html body .cmponofftext,` +
      `html body .cmpwelcomeprpstxt,html body .cmpboxrecalltxt{color:${p.ink} !important}` +

    // Filled buttons: "Alle akzeptieren", in the banner and in the dialog.
    // Their background comes from --clrButton above; the design hardcodes only
    // the text colour. "Speichern" carries `cmpboxbtnyes` AS WELL AS
    // `cmpboxbtnyescustomchoices` and is drawn as an outline button, so it is
    // excluded here — without the :not() the `A.cmpboxbtnyes` selector
    // out-specifies the outline rule below and paints its label white on
    // white.
    `html body .cmpboxbtnyes:not(.cmpboxbtnyescustomchoices),` +
      `html body A.cmpboxbtnyes:not(.cmpboxbtnyescustomchoices),` +
      `html body A.cmpboxbtnyes:not(.cmpboxbtnyescustomchoices):link,` +
      `html body .cmpboxbtnacceptcustomchoices{color:${p.buttonOn} !important}` +

    // Outline buttons: "Einstellungen", "Speichern", "Ablehnen".
    `html body .cmpboxbtncustom,html body .cmpboxbtnyescustomchoices,` +
      `html body A.cmpboxbtnyescustomchoices,html body A.cmpboxbtnyescustomchoices:link,` +
      `html body .cmpboxbtnno{background-color:${p.surface} !important;` +
      `color:${p.ink} !important;border-color:${p.line} !important}` +

    // Settings dialog: tab strip, purpose/vendor tables and their hairlines.
    // `.cmpallprps` keeps text = background, as the design has it (the label
    // is meant to be invisible there).
    `html body .cmpboxnaviitemactive{background-color:${p.highlight} !important}` +
    `html body .cmpallprps{background-color:${p.surface} !important;color:${p.surface} !important}` +
    `html body .cmpallvend{background-color:${p.surface} !important;border-bottom-color:${p.line} !important}` +
    `html body .cmptdborder,html body .cmpvendorbox2{border-bottom-color:${p.line} !important}` +

    toggleTracks
  )
}

/**
 * URLs are interpolated into an inline `<script>`. Only absolute https URLs
 * pass, and `<` is escaped so a value can never close the element.
 */
function jsStringLiteral(value: string): string {
  return JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029')
}

function httpsUrl(value: string | null | undefined): string | null {
  if (typeof value !== 'string' || value.trim() === '') return null
  try {
    const url = new URL(value.trim())
    return url.protocol === 'https:' ? url.toString() : null
  } catch {
    return null
  }
}

/**
 * The CMP's documented client-side variables for the legal links. Must be
 * assigned BEFORE the loader runs, which is why the caller prepends this to
 * the loader's own inline script instead of adding a second `<Script>`
 * (`next/script` gives no ordering guarantee between two lazy scripts).
 *
 * Returns '' for a client that brings no https legal URLs, leaving the
 * banner's links on the backend's defaults.
 */
export function buildCmpClientConfig(
  client: CmpBrandingClient | null | undefined
): string {
  if (client == null) return ''

  const assignments: string[] = []
  const privacy = httpsUrl(client.privacyUrl)
  const imprint = httpsUrl(client.imprintUrl)
  const terms = httpsUrl(client.termsOfServiceUrl)

  if (privacy != null) assignments.push(`window.cmp_privacyurl=${jsStringLiteral(privacy)};`)
  if (imprint != null) assignments.push(`window.cmp_imprinturl=${jsStringLiteral(imprint)};`)
  if (terms != null) assignments.push(`window.cmp_tacurl=${jsStringLiteral(terms)};`)

  return assignments.join('')
}
